"use client"

import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  parseTimeZone,
  TIME_ZONE_COOKIE,
  writeTimeZoneCookie,
} from "./timeZone"

const TimeZoneContext = createContext<string | undefined>(undefined)

const readTimeZoneCookie = (): string | null =>
  parseTimeZone(
    document.cookie
      .split("; ")
      .find((entry) => entry.startsWith(`${TIME_ZONE_COOKIE}=`))
      ?.slice(TIME_ZONE_COOKIE.length + 1),
  )

/**
 * Provides the viewer's device time zone (an IANA name like
 * `"America/New_York"`) to client components, resolved once after mount.
 *
 * Before mount the value is `undefined`, so date renderers fall back to the
 * request's UTC formatter (see `request.ts`) — keeping the server output and
 * the first client paint identical, so there is no hydration mismatch. A single
 * coordinated re-render then flips the whole tree to the local zone.
 *
 * Why client-side and not `getRequestConfig`: resolving the zone on the server
 * means reading a request header/cookie in the same config that produces the
 * translation `messages`, which makes `getTranslations()` request-time across
 * the app and collapses the Cache Components static shell. The device zone is
 * also more accurate than an IP guess. See `lib/i18n/AGENTS.md` ("Time zones").
 *
 * Also mirrors the resolved zone into the `siro-timezone` cookie (same pattern
 * as `LocaleSync`) so server-side calendar-day math — e.g. the recordings date
 * filter — can anchor a picked day to the viewer's real local midnight instead
 * of UTC. This cookie is the authoritative input to `getServerTimeZone()`;
 * until it exists, that resolver falls back to an IP guess and then UTC.
 */
export const TimeZoneProvider = ({ children }: { children: ReactNode }) => {
  const [timeZone, setTimeZone] = useState<string | undefined>(undefined)

  useEffect(() => {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone
    setTimeZone(resolved)
    // Write only on drift, so the steady state touches nothing.
    if (readTimeZoneCookie() !== resolved) writeTimeZoneCookie(resolved)
  }, [])

  return (
    <TimeZoneContext.Provider value={timeZone}>
      {children}
    </TimeZoneContext.Provider>
  )
}

/**
 * The viewer's device time zone, or `undefined` until resolved after mount.
 * Prefer `<LocalDateTime>` / `useLocalFormatter` over reading this directly.
 */
export const useTimeZone = (): string | undefined => useContext(TimeZoneContext)
